import { FormHelperText, InputAdornment, MenuItem, TextField, TextFieldProps, useTheme } from '@mui/material'
import { Field, FieldProps } from 'formik'

import useStyles from '../formik-input/index.styles'

import useDropdownStyles from './index.styles'

type IProps = {
  name: string // formik field name
  label: string
  required: boolean
  items: { id: string; [x: string]: string }[] // list of items to render in dropdown
  disabled?: boolean
  hideErrors?: boolean
  displayField?: string // a field we want to display as label, defaults to "name"
  startAdornment?: React.ReactNode // icon displayed at the start of the dropdown
  endAdornment?: () => React.ReactNode // endAdornment component, if it's needed to be displayed in dropdown - will be displayed next to the dropdown Arrow Down icon
} & TextFieldProps

const FormikDropdown = (props: IProps) => {
  const { name, label, required, disabled, items, startAdornment, endAdornment, size, variant, hideErrors = false, ...rest } = props
  const displayField = props.displayField ?? 'name'

  const classes = useStyles
  const dropdownClasses = useDropdownStyles
  const theme = useTheme()

  const handleChange = (
    event: React.ChangeEvent<{ value: unknown }>,
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean | undefined) => void
  ) => {
    setFieldValue(name, event.target.value)
  }

  return (
    <Field name={name}>
      {({ field, form }: FieldProps) => {
        const { errors, touched } = form
        const hasError = Boolean(errors[name] && touched[name])

        return (
          <>
            <TextField
              fullWidth={true}
              {...rest}
              {...field}
              select={true}
              error={hasError}
              slotProps={{
                htmlInput: {
                  id: `${name}-select`,
                  'data-testid': `${name}-select`,
                  'aria-labelledby': `${name}-select-label`,
                  'aria-label': label,
                },
                input: {
                  startAdornment: startAdornment ? <InputAdornment position="start">{startAdornment}</InputAdornment> : undefined,
                  endAdornment: endAdornment ? (
                    <InputAdornment sx={dropdownClasses.endAdornment} position="end">
                      {endAdornment()}
                    </InputAdornment>
                  ) : undefined,
                },
                inputLabel: {
                  htmlFor: `${name}-select`,
                  id: `${name}-select-label`,
                },
              }}
              sx={{
                ...dropdownClasses.dropdown(theme, disabled),
                ...props.sx,
              }}
              label={`${label}${required ? '*' : ''}`}
              margin="normal"
              size={size ?? 'small'}
              variant={variant ?? 'outlined'}
              onChange={(e) => handleChange(e, form.setFieldValue)}
              disabled={disabled}
            >
              {items.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item[displayField]}
                </MenuItem>
              ))}
            </TextField>

            {!hideErrors && hasError && (
              <FormHelperText data-testid={`${name}-error-text`} sx={classes.errorText}>
                {errors[name] as string}
              </FormHelperText>
            )}
          </>
        )
      }}
    </Field>
  )
}

export default FormikDropdown
